import React, { useEffect, useState } from "react";
import { FirebaseAnalytics } from "@ionic-native/firebase-analytics";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import Title from "components/Title";
import Button from "components/Button";
import { Charge } from "model";
import api from "../../api";
import { tryCatch } from "../../utils";

const ChargesContainer: React.FC<{}> = () => {
  const { t } = useTranslation("charges");
  const [charges, setCharges] = useState<Charge[]>([]);
  const [showAll, setShowAll] = useState<Boolean>(false);
  const [balance, setBalance] = useState<Number>(0);

  useEffect(() => {
    FirebaseAnalytics.logEvent("init_page", { page: "charges" });
    getChargeList();
  }, []);

  const getChargeList = async () => {
    let chargeListRes = await tryCatch(api.subscription.getChargeList());
    if (chargeListRes.error) {
      console.log("Error fetching charges: ", chargeListRes.error);
    } else {
      setCharges(chargeListRes.result.data.charges);
      setBalance(chargeListRes.result.data.balance);
    }
  };

  return (
    <div className="py-0 px-8 mx-[-32px]">
      <Title
        title={t("title")}
        subtitle={`${charges.length} ${t("subtitle")}${
          charges.length === 1 ? "" : "s"
        } Available - ${
          showAll || charges.length < 5 ? t("showingAll") : t("showingRecent")
        }`}
        className="mt-7"
      />
      {Number(balance) < 0 && (
        <div
          className="py-4"
          style={{ border: "1px solid d8d8d8" }}
        >{`You have a credit balance of $${Number(-balance / 100).toFixed(
          2
        )}. This amount will be applied to your next payment automatically.`}</div>
      )}
      {charges.length > 0 && (
        <ul className="list-none pl-0">
          <div
            style={{
              display: "flex",
              padding: "20px 0px",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex" }}>
              <div
                style={{
                  width: "40%",
                  display: "inline-block",
                  alignItems: "center",
                }}
              >
                {t("description")}
              </div>
              <div
                style={{
                  width: "20%",
                  display: "inline-block",
                  alignItems: "center",
                }}
              >
                {t("created")}
              </div>
              <div
                style={{
                  width: "20%",
                  display: "inline-block",
                  alignItems: "center",
                }}
              >
                {t("amount")}
              </div>
              <div
                style={{
                  width: "20%",
                  display: "inline-block",
                  alignItems: "center",
                }}
              >
                {t("receipt")}
              </div>
            </div>
            {charges.map((charge, i) => {
              if (showAll || i < 5) {
                return (
                  <ChargeRow charge={charge} key={`${charge.charge_id}_${i}`} />
                );
              } else {
                return null;
              }
            })}
          </div>
        </ul>
      )}
      {charges.length > 5 && (
        <button
          className={classNames(
            "hover:cursor-allowed group transition text-gray-500 hover:text-gray-900  border-b border-solid border-gray-600  rounded-sm no-underline"
          )}
          onClick={() => {
            setShowAll(!showAll);
          }}
        >
          {!showAll ? t("showAll") : t("hideAll")}
        </button>
      )}
    </div>
  );
};

interface ChargeRowProps {
  charge: Charge;
}

const ChargeRow: React.FC<ChargeRowProps> = ({ charge }) => {
  const { t } = useTranslation("charges");

  return (
    <div
      style={{ display: "flex", alignItems: "center" }}
      className={"text-gray-500"}
      key={`${charge.charge_id}`}
    >
      <div
        style={{
          width: "40%",
          display: "inline-block",
          alignItems: "center",
          wordWrap: "break-word",
        }}
      >
        <p className="sm:inline">{charge.description || "See Receipt"}</p>
      </div>
      <div
        style={{
          width: "20%",
          display: "inline-block",
          alignItems: "center",
          wordWrap: "break-word",
        }}
      >
        <p className="sm:inline">
          {new Date(charge.created * 1000).toLocaleDateString()}
        </p>
      </div>
      <div
        style={{
          width: "20%",
          display: "inline-block",
          alignItems: "center",
          wordWrap: "break-word",
        }}
      >
        <p className="sm:inline">{`$ ${Number(charge.amount / 100).toFixed(
          2
        )}`}</p>
      </div>
      <div
        style={{
          width: "20%",
          display: "inline-block",
          alignItems: "center",
          wordWrap: "break-word",
        }}
      >
        {charge.receipt_url && (
          <Button
            data-testid="openReceipt"
            size="small"
            onClick={() => {
              window.open(charge.receipt_url, "_blank");
            }}
          >
            {t("receipt")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChargesContainer;
